/**
 * Admin API client - connects to go-api /v1/admin/* endpoints
 */

const base = () =>
  (process.env.NEXT_PUBLIC_API_URL || "http://localhost:8080/v1").replace(/\/$/, "");

const ADMIN_API_KEY = process.env.NEXT_PUBLIC_ADMIN_API_KEY || "";

export function getAdminKey(): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|; )admin_key=([^;]*)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export function setAdminKey(key: string): void {
  if (typeof document === "undefined") return;
  document.cookie = `admin_key=${encodeURIComponent(key)}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
}

export function clearAdminKey(): void {
  if (typeof document === "undefined") return;
  document.cookie = "admin_key=; path=/; max-age=0";
}

export async function adminLogin(apiKey: string): Promise<{ success: boolean; error?: string }> {
  // Validate API key by calling a protected endpoint
  try {
    const response = await fetch(`${base()}/admin/stats/overview`, {
      headers: { 'X-Internal-API-Key': apiKey }
    });

    if (response.ok) {
      setAdminKey(apiKey);
      return { success: true };
    }
    return { success: false, error: 'Invalid API key' };
  } catch (error) {
    return { success: false, error: 'Failed to connect to API' };
  }
}

export async function adminFetch(
  path: string,
  init?: RequestInit & { json?: unknown }
): Promise<Response> {
  const apiKey = getAdminKey() || ADMIN_API_KEY;
  const headers: HeadersInit = {
    ...(init?.headers as Record<string, string>),
    ...(init?.json !== undefined ? { "Content-Type": "application/json" } : {}),
  };
  if (apiKey) {
    (headers as Record<string, string>)["X-Internal-API-Key"] = apiKey;
  }
  const body =
    init?.json !== undefined ? JSON.stringify(init.json) : init?.body;
  return fetch(`${base()}${path.startsWith("/") ? path : `/${path}`}`, {
    ...init,
    headers,
    body,
  });
}

// API client functions for admin endpoints

interface PlatformOverviewResponse {
  total_merchants?: number;
  active_merchants?: number;
  total_customers?: number;
  active_customers_24h?: number;
  total_games_played?: number;
  games_played_24h?: number;
  qr_scans_total?: number;
  qr_scans_24h?: number;
  revenue_mtd?: number;
  revenue_current_month?: number;
  TotalMerchants?: number;
  ActiveMerchants?: number;
  TotalCustomers?: number;
  ActiveCustomers24h?: number;
  TotalGamesPlayed?: number;
  GamesPlayed24h?: number;
  QRScansTotal?: number;
  QRScans24h?: number;
  RevenueMTD?: number;
  RevenueCurrentMonth?: number;
}

function asNumber(value: unknown): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export async function getPlatformOverview() {
  const response = await adminFetch("/admin/stats/overview");
  if (!response.ok) throw new Error("Failed to fetch platform overview");
  const data = (await response.json()) as PlatformOverviewResponse;

  // Support both legacy snake_case and current PascalCase API payloads.
  return {
    total_merchants: asNumber(data.total_merchants ?? data.TotalMerchants),
    active_merchants: asNumber(data.active_merchants ?? data.ActiveMerchants),
    total_customers: asNumber(data.total_customers ?? data.TotalCustomers),
    active_customers_24h: asNumber(data.active_customers_24h ?? data.ActiveCustomers24h),
    total_games_played: asNumber(data.total_games_played ?? data.TotalGamesPlayed),
    games_played_24h: asNumber(data.games_played_24h ?? data.GamesPlayed24h),
    qr_scans_total: asNumber(data.qr_scans_total ?? data.QRScansTotal),
    qr_scans_24h: asNumber(data.qr_scans_24h ?? data.QRScans24h),
    revenue_mtd: asNumber(data.revenue_mtd ?? data.RevenueMTD),
    revenue_current_month: asNumber(
      data.revenue_current_month ?? data.RevenueCurrentMonth
    ),
  };
}

export async function listMerchants(params: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  sort?: string;
}) {
  const query = new URLSearchParams();
  if (params.page) query.set("page", params.page.toString());
  if (params.limit) query.set("limit", params.limit.toString());
  if (params.search) query.set("search", params.search);
  if (params.status) query.set("status", params.status);
  if (params.sort) query.set("sort", params.sort);

  const response = await adminFetch(`/admin/merchants?${query.toString()}`);
  if (!response.ok) throw new Error("Failed to fetch merchants");
  const data = (await response.json()) as {
    merchants?: Array<{
      id?: string;
      business_name?: string;
      email?: string;
      plan_name?: string;
      status?: string;
      created_at?: string;
      qr_campaigns_count?: number;
      customers_count?: number;
      games_played_total?: number;
      revenue_total?: number;
      ID?: string;
      BusinessName?: string;
      Email?: string;
      PlanName?: string;
      Status?: string;
      CreatedAt?: string;
      QRCampaignsCount?: number;
      CustomersCount?: number;
      GamesPlayedTotal?: number;
      RevenueTotal?: number;
    }>;
    total?: number;
    page?: number;
    limit?: number;
  };

  return {
    merchants: (data.merchants || []).map((merchant) => ({
      id: String(merchant.id ?? merchant.ID ?? ""),
      business_name: String(merchant.business_name ?? merchant.BusinessName ?? ""),
      email: String(merchant.email ?? merchant.Email ?? ""),
      plan_name: String(merchant.plan_name ?? merchant.PlanName ?? ""),
      status: String(merchant.status ?? merchant.Status ?? ""),
      created_at: String(merchant.created_at ?? merchant.CreatedAt ?? ""),
      qr_campaigns_count: asNumber(
        merchant.qr_campaigns_count ?? merchant.QRCampaignsCount
      ),
      customers_count: asNumber(merchant.customers_count ?? merchant.CustomersCount),
      games_played_total: asNumber(
        merchant.games_played_total ?? merchant.GamesPlayedTotal
      ),
      revenue_total: asNumber(merchant.revenue_total ?? merchant.RevenueTotal),
    })),
    total: asNumber(data.total),
    page: asNumber(data.page),
    limit: asNumber(data.limit),
  };
}

export async function getMerchant(merchantId: string) {
  const response = await adminFetch(`/admin/merchants/${merchantId}`);
  if (!response.ok) throw new Error("Failed to fetch merchant");
  const data = (await response.json()) as {
    id?: string;
    business_name?: string;
    contact_name?: string;
    email?: string;
    phone?: string;
    logo_url?: string;
    address?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
    website?: string;
    industry?: string;
    description?: string;
    timezone?: string;
    stripe_customer_id?: string;
    created_at?: string;
    updated_at?: string;
    plan_name?: string;
    subscription_status?: string;
    current_period_end?: string | null;
    cancel_at_period_end?: boolean;
    qr_campaigns_count?: number;
    customers_count?: number;
    staff_count?: number;
    games_played_total?: number;
    revenue_total?: number;
    ID?: string;
    BusinessName?: string;
    ContactName?: string;
    Email?: string;
    Phone?: string;
    LogoURL?: string;
    Address?: string;
    City?: string;
    State?: string;
    PostalCode?: string;
    Country?: string;
    Website?: string;
    Industry?: string;
    Description?: string;
    Timezone?: string;
    StripeCustomerID?: string;
    CreatedAt?: string;
    UpdatedAt?: string;
    PlanName?: string;
    SubscriptionStatus?: string;
    CurrentPeriodEnd?: string | null;
    CancelAtPeriodEnd?: boolean;
    QRCampaignsCount?: number;
    CustomersCount?: number;
    StaffCount?: number;
    GamesPlayedTotal?: number;
    RevenueTotal?: number;
  };

  return {
    id: String(data.id ?? data.ID ?? ""),
    business_name: String(data.business_name ?? data.BusinessName ?? ""),
    contact_name: String(data.contact_name ?? data.ContactName ?? ""),
    email: String(data.email ?? data.Email ?? ""),
    phone: String(data.phone ?? data.Phone ?? ""),
    logo_url: String(data.logo_url ?? data.LogoURL ?? ""),
    address: String(data.address ?? data.Address ?? ""),
    city: String(data.city ?? data.City ?? ""),
    state: String(data.state ?? data.State ?? ""),
    postal_code: String(data.postal_code ?? data.PostalCode ?? ""),
    country: String(data.country ?? data.Country ?? ""),
    website: String(data.website ?? data.Website ?? ""),
    industry: String(data.industry ?? data.Industry ?? ""),
    description: String(data.description ?? data.Description ?? ""),
    timezone: String(data.timezone ?? data.Timezone ?? ""),
    stripe_customer_id: String(data.stripe_customer_id ?? data.StripeCustomerID ?? ""),
    created_at: String(data.created_at ?? data.CreatedAt ?? ""),
    updated_at: String(data.updated_at ?? data.UpdatedAt ?? ""),
    plan_name: String(data.plan_name ?? data.PlanName ?? ""),
    subscription_status: String(
      data.subscription_status ?? data.SubscriptionStatus ?? ""
    ),
    current_period_end:
      data.current_period_end !== undefined
        ? data.current_period_end
        : data.CurrentPeriodEnd !== undefined
          ? data.CurrentPeriodEnd
          : null,
    cancel_at_period_end:
      typeof data.cancel_at_period_end === "boolean"
        ? data.cancel_at_period_end
        : typeof data.CancelAtPeriodEnd === "boolean"
          ? data.CancelAtPeriodEnd
          : false,
    qr_campaigns_count: asNumber(data.qr_campaigns_count ?? data.QRCampaignsCount),
    customers_count: asNumber(data.customers_count ?? data.CustomersCount),
    staff_count: asNumber(data.staff_count ?? data.StaffCount),
    games_played_total: asNumber(data.games_played_total ?? data.GamesPlayedTotal),
    revenue_total: asNumber(data.revenue_total ?? data.RevenueTotal),
  };
}

export async function getMerchantAnalytics(merchantId: string, days: number = 30) {
  const response = await adminFetch(`/admin/merchants/${merchantId}/analytics?days=${days}`);
  if (!response.ok) throw new Error("Failed to fetch merchant analytics");
  return response.json();
}

export async function getMerchantActivity(merchantId: string, limit: number = 20) {
  const response = await adminFetch(`/admin/merchants/${merchantId}/activity?limit=${limit}`);
  if (!response.ok) throw new Error("Failed to fetch merchant activity");
  return response.json();
}

export async function listCustomers(params: {
  page?: number;
  limit?: number;
  search?: string;
  merchantId?: string;
}) {
  const query = new URLSearchParams();
  if (params.page) query.set("page", params.page.toString());
  if (params.limit) query.set("limit", params.limit.toString());
  if (params.search) query.set("search", params.search);
  if (params.merchantId) query.set("merchant_id", params.merchantId);

  const response = await adminFetch(`/admin/customers?${query.toString()}`);
  if (!response.ok) throw new Error("Failed to fetch customers");
  const data = (await response.json()) as {
    customers?: Array<{
      id?: string;
      name?: string;
      phone?: string;
      email?: string;
      merchant_id?: string;
      merchant_name?: string;
      total_points?: number;
      games_played?: number;
      last_active?: string | null;
      created_at?: string;
      ID?: string;
      Name?: string;
      Phone?: string;
      Email?: string;
      MerchantID?: string;
      MerchantName?: string;
      TotalPoints?: number;
      GamesPlayed?: number;
      LastActive?: string | null;
      CreatedAt?: string;
    }>;
    total?: number;
    page?: number;
    limit?: number;
  };

  return {
    customers: (data.customers || []).map((customer) => ({
      id: String(customer.id ?? customer.ID ?? ""),
      name: String(customer.name ?? customer.Name ?? ""),
      phone: String(customer.phone ?? customer.Phone ?? ""),
      email: String(customer.email ?? customer.Email ?? ""),
      merchant_id: String(customer.merchant_id ?? customer.MerchantID ?? ""),
      merchant_name: String(customer.merchant_name ?? customer.MerchantName ?? ""),
      total_points: asNumber(customer.total_points ?? customer.TotalPoints),
      games_played: asNumber(customer.games_played ?? customer.GamesPlayed),
      last_active:
        customer.last_active !== undefined
          ? customer.last_active
          : customer.LastActive !== undefined
            ? customer.LastActive
            : null,
      created_at: String(customer.created_at ?? customer.CreatedAt ?? ""),
    })),
    total: asNumber(data.total),
    page: asNumber(data.page),
    limit: asNumber(data.limit),
  };
}

export async function getCustomer(customerId: string) {
  const response = await adminFetch(`/admin/customers/${customerId}`);
  if (!response.ok) throw new Error("Failed to fetch customer");
  const data = (await response.json()) as {
    id?: string;
    name?: string;
    phone?: string;
    email?: string;
    instagram?: string;
    avatar_url?: string;
    merchant_id?: string;
    merchant_name?: string;
    total_points?: number;
    games_played?: number;
    campaigns_participated?: number;
    last_play_date?: string | null;
    first_play_date?: string | null;
    created_at?: string;
    ID?: string;
    Name?: string;
    Phone?: string;
    Email?: string;
    Instagram?: string;
    AvatarURL?: string;
    MerchantID?: string;
    MerchantName?: string;
    TotalPoints?: number;
    GamesPlayed?: number;
    CampaignsParticipated?: number;
    LastPlayDate?: string | null;
    FirstPlayDate?: string | null;
    CreatedAt?: string;
  };

  return {
    id: String(data.id ?? data.ID ?? ""),
    name: String(data.name ?? data.Name ?? ""),
    phone: String(data.phone ?? data.Phone ?? ""),
    email: String(data.email ?? data.Email ?? ""),
    instagram: String(data.instagram ?? data.Instagram ?? ""),
    avatar_url: String(data.avatar_url ?? data.AvatarURL ?? ""),
    merchant_id: String(data.merchant_id ?? data.MerchantID ?? ""),
    merchant_name: String(data.merchant_name ?? data.MerchantName ?? ""),
    total_points: asNumber(data.total_points ?? data.TotalPoints),
    games_played: asNumber(data.games_played ?? data.GamesPlayed),
    campaigns_participated: asNumber(
      data.campaigns_participated ?? data.CampaignsParticipated
    ),
    last_play_date:
      data.last_play_date !== undefined
        ? data.last_play_date
        : data.LastPlayDate !== undefined
          ? data.LastPlayDate
          : null,
    first_play_date:
      data.first_play_date !== undefined
        ? data.first_play_date
        : data.FirstPlayDate !== undefined
          ? data.FirstPlayDate
          : null,
    created_at: String(data.created_at ?? data.CreatedAt ?? ""),
  };
}

export async function getCustomerSessions(customerId: string, page: number = 1, limit: number = 20) {
  const response = await adminFetch(`/admin/customers/${customerId}/sessions?page=${page}&limit=${limit}`);
  if (!response.ok) throw new Error("Failed to fetch customer sessions");
  const data = (await response.json()) as {
    sessions?: Array<{
      id?: string;
      game_type?: string;
      score?: number;
      points_earned?: number;
      session_duration?: number;
      was_completed?: boolean;
      started_at?: string;
      completed_at?: string | null;
      ID?: string;
      GameType?: string;
      Score?: number;
      PointsEarned?: number;
      SessionDuration?: number;
      WasCompleted?: boolean;
      StartedAt?: string;
      CompletedAt?: string | null;
    }>;
    total?: number;
    page?: number;
    limit?: number;
  };

  return {
    sessions: (data.sessions || []).map((session) => ({
      id: String(session.id ?? session.ID ?? ""),
      game_type: String(session.game_type ?? session.GameType ?? ""),
      score: asNumber(session.score ?? session.Score),
      points_earned: asNumber(session.points_earned ?? session.PointsEarned),
      session_duration: asNumber(session.session_duration ?? session.SessionDuration),
      was_completed:
        typeof session.was_completed === "boolean"
          ? session.was_completed
          : typeof session.WasCompleted === "boolean"
            ? session.WasCompleted
            : false,
      started_at: String(session.started_at ?? session.StartedAt ?? ""),
      completed_at:
        session.completed_at !== undefined
          ? session.completed_at
          : session.CompletedAt !== undefined
            ? session.CompletedAt
            : null,
    })),
    total: asNumber(data.total),
    page: asNumber(data.page),
    limit: asNumber(data.limit),
  };
}

export async function listAuditLogs(params: {
  page?: number;
  limit?: number;
  merchantId?: string;
  customerId?: string;
  action?: string;
  from?: string;
  to?: string;
}) {
  const query = new URLSearchParams();
  if (params.page) query.set("page", params.page.toString());
  if (params.limit) query.set("limit", params.limit.toString());
  if (params.merchantId) query.set("merchant_id", params.merchantId);
  if (params.customerId) query.set("customer_id", params.customerId);
  if (params.action) query.set("action", params.action);
  if (params.from) query.set("from", params.from);
  if (params.to) query.set("to", params.to);

  const response = await adminFetch(`/admin/audit/logs?${query.toString()}`);
  if (!response.ok) {
    return {
      logs: [],
      total: 0,
      page: asNumber(params.page ?? 1),
      limit: asNumber(params.limit ?? 100),
    };
  }

  const data = (await response.json()) as {
    logs?: Array<{
      id?: string;
      timestamp?: string;
      action?: string;
      actor_type?: string;
      actor_id?: string;
      merchant_id?: string | null;
      ip_address?: string;
      user_agent?: string;
      details?: string;
      ID?: string;
      Timestamp?: string;
      Action?: string;
      ActorType?: string;
      ActorID?: string;
      MerchantID?: string | null;
      IPAddress?: string;
      UserAgent?: string;
      Details?: string;
    }>;
    total?: number;
    page?: number;
    limit?: number;
  };

  return {
    logs: (data.logs || []).map((log) => ({
      id: String(log.id ?? log.ID ?? ""),
      timestamp: String(log.timestamp ?? log.Timestamp ?? ""),
      action: String(log.action ?? log.Action ?? ""),
      actor_type: String(log.actor_type ?? log.ActorType ?? ""),
      actor_id: String(log.actor_id ?? log.ActorID ?? ""),
      merchant_id:
        log.merchant_id !== undefined
          ? log.merchant_id
          : log.MerchantID !== undefined
            ? log.MerchantID
            : null,
      ip_address: String(log.ip_address ?? log.IPAddress ?? ""),
      user_agent: String(log.user_agent ?? log.UserAgent ?? ""),
      details: String(log.details ?? log.Details ?? ""),
    })),
    total: asNumber(data.total),
    page: asNumber(data.page),
    limit: asNumber(data.limit),
  };
}

export async function listSessions(params: {
  page?: number;
  limit?: number;
  merchantId?: string;
  gameCode?: string;
  status?: string;
}) {
  const query = new URLSearchParams();
  if (params.page) query.set("page", params.page.toString());
  if (params.limit) query.set("limit", params.limit.toString());
  if (params.merchantId) query.set("merchant_id", params.merchantId);
  if (params.gameCode) query.set("game_code", params.gameCode);
  if (params.status) query.set("status", params.status);

  const response = await adminFetch(`/admin/sessions?${query.toString()}`);
  if (!response.ok) throw new Error("Failed to fetch sessions");
  const data = (await response.json()) as {
    sessions?: Array<{
      id?: string;
      merchant_id?: string;
      merchant_name?: string;
      customer_id?: string;
      customer_name?: string;
      game_type?: string;
      score?: number;
      points_earned?: number;
      completed_at?: string | null;
      session_duration?: number;
      was_completed?: boolean;
      ID?: string;
      MerchantID?: string;
      MerchantName?: string;
      CustomerID?: string;
      CustomerName?: string;
      GameType?: string;
      Score?: number;
      PointsEarned?: number;
      CompletedAt?: string | null;
      SessionDuration?: number;
      WasCompleted?: boolean;
    }>;
    total?: number;
    page?: number;
    limit?: number;
  };

  return {
    sessions: (data.sessions || []).map((session) => ({
      id: String(session.id ?? session.ID ?? ""),
      merchant_id: String(session.merchant_id ?? session.MerchantID ?? ""),
      merchant_name: String(session.merchant_name ?? session.MerchantName ?? ""),
      customer_id: String(session.customer_id ?? session.CustomerID ?? ""),
      customer_name: String(session.customer_name ?? session.CustomerName ?? ""),
      game_type: String(session.game_type ?? session.GameType ?? ""),
      score: asNumber(session.score ?? session.Score),
      points_earned: asNumber(session.points_earned ?? session.PointsEarned),
      completed_at:
        session.completed_at !== undefined
          ? session.completed_at
          : session.CompletedAt !== undefined
            ? session.CompletedAt
            : null,
      session_duration: asNumber(session.session_duration ?? session.SessionDuration),
      was_completed:
        typeof session.was_completed === "boolean"
          ? session.was_completed
          : typeof session.WasCompleted === "boolean"
            ? session.WasCompleted
            : false,
    })),
    total: asNumber(data.total),
    page: asNumber(data.page),
    limit: asNumber(data.limit),
  };
}

export async function listQRCampaigns(params: {
  page?: number;
  limit?: number;
  merchantId?: string;
  status?: string;
}) {
  const query = new URLSearchParams();
  if (params.page) query.set("page", params.page.toString());
  if (params.limit) query.set("limit", params.limit.toString());
  if (params.merchantId) query.set("merchant_id", params.merchantId);
  if (params.status) query.set("status", params.status);

  const response = await adminFetch(`/admin/qr-campaigns?${query.toString()}`);
  if (!response.ok) throw new Error("Failed to fetch QR campaigns");
  const data = (await response.json()) as {
    campaigns?: Array<{
      id?: string;
      merchant_id?: string;
      merchant_name?: string;
      campaign_name?: string;
      game_code?: string;
      status?: string;
      scans_count?: number;
      unique_scans?: number;
      participants?: number;
      created_at?: string;
      activated_at?: string | null;
      ID?: string;
      MerchantID?: string;
      MerchantName?: string;
      CampaignName?: string;
      GameCode?: string;
      Status?: string;
      ScansCount?: number;
      UniqueScans?: number;
      Participants?: number;
      CreatedAt?: string;
      ActivatedAt?: string | null;
    }>;
    total?: number;
    page?: number;
    limit?: number;
  };

  return {
    campaigns: (data.campaigns || []).map((campaign) => ({
      id: String(campaign.id ?? campaign.ID ?? ""),
      merchant_id: String(campaign.merchant_id ?? campaign.MerchantID ?? ""),
      merchant_name: String(campaign.merchant_name ?? campaign.MerchantName ?? ""),
      campaign_name: String(campaign.campaign_name ?? campaign.CampaignName ?? ""),
      game_code: String(campaign.game_code ?? campaign.GameCode ?? ""),
      status: String(campaign.status ?? campaign.Status ?? ""),
      scans_count: asNumber(campaign.scans_count ?? campaign.ScansCount),
      unique_scans: asNumber(campaign.unique_scans ?? campaign.UniqueScans),
      participants: asNumber(campaign.participants ?? campaign.Participants),
      created_at: String(campaign.created_at ?? campaign.CreatedAt ?? ""),
      activated_at:
        campaign.activated_at !== undefined
          ? campaign.activated_at
          : campaign.ActivatedAt !== undefined
            ? campaign.ActivatedAt
            : null,
    })),
    total: asNumber(data.total),
    page: asNumber(data.page),
    limit: asNumber(data.limit),
  };
}

export async function getBillingOverview() {
  const response = await adminFetch("/admin/billing/overview");
  if (!response.ok) throw new Error("Failed to fetch billing overview");
  return response.json();
}

export async function listInvoices(params: {
  page?: number;
  limit?: number;
  status?: string;
  merchantId?: string;
}) {
  const query = new URLSearchParams();
  if (params.page) query.set("page", params.page.toString());
  if (params.limit) query.set("limit", params.limit.toString());
  if (params.status) query.set("status", params.status);
  if (params.merchantId) query.set("merchant_id", params.merchantId);

  const response = await adminFetch(`/admin/invoices?${query.toString()}`);
  if (!response.ok) throw new Error("Failed to fetch invoices");
  const data = (await response.json()) as {
    invoices?: Array<{
      id?: string;
      merchant_id?: string;
      merchant_name?: string;
      stripe_invoice_id?: string;
      invoice_number?: string;
      status?: string;
      total?: number;
      currency?: string;
      due_date?: string | null;
      paid_at?: string | null;
      created_at?: string;
      ID?: string;
      MerchantID?: string;
      MerchantName?: string;
      StripeInvoiceID?: string;
      InvoiceNumber?: string;
      Status?: string;
      Total?: number;
      Currency?: string;
      DueDate?: string | null;
      PaidAt?: string | null;
      CreatedAt?: string;
    }>;
    total?: number;
    page?: number;
    limit?: number;
  };

  return {
    invoices: (data.invoices || []).map((invoice) => ({
      id: String(invoice.id ?? invoice.ID ?? ""),
      merchant_id: String(invoice.merchant_id ?? invoice.MerchantID ?? ""),
      merchant_name: String(invoice.merchant_name ?? invoice.MerchantName ?? ""),
      stripe_invoice_id: String(
        invoice.stripe_invoice_id ?? invoice.StripeInvoiceID ?? ""
      ),
      invoice_number: String(invoice.invoice_number ?? invoice.InvoiceNumber ?? ""),
      status: String(invoice.status ?? invoice.Status ?? ""),
      total: asNumber(invoice.total ?? invoice.Total),
      currency: String(invoice.currency ?? invoice.Currency ?? ""),
      due_date:
        invoice.due_date !== undefined
          ? invoice.due_date
          : invoice.DueDate !== undefined
            ? invoice.DueDate
            : null,
      paid_at:
        invoice.paid_at !== undefined
          ? invoice.paid_at
          : invoice.PaidAt !== undefined
            ? invoice.PaidAt
            : null,
      created_at: String(invoice.created_at ?? invoice.CreatedAt ?? ""),
    })),
    total: asNumber(data.total),
    page: asNumber(data.page),
    limit: asNumber(data.limit),
  };
}
